/**
 * Moldura e peças visuais do estudo "Por que consórcio": cores, a folha A4 do
 * PDF, a seção de largura inteira do link (modo tela) e os cartões, tabelas e
 * caixas de gráfico que as páginas de estudoPaginas.ts montam.
 */
import type { Estudo } from './estudo';

export const COR = {
  noite: '#0F1A24',
  marinho: '#1F2C40',
  azul: '#354660',
  azulClaro: '#7F9BC0',
  ouro: '#C4AD8E',
  ouroClaro: '#F3EDE3',
  fundo: '#F4F6F9',
  texto: '#1A2433',
  suave: '#6B7685',
  verde: '#2F8F5B',
  vermelho: '#C0392B',
};

export const FONTE_PDF = "'Montserrat', 'Helvetica Neue', Arial, sans-serif";

export const LARGURA = 794;
export const ALTURA = 1123;
/** Largura do conteúdo no modo tela, antes do zoom; o PDF usa 794 − 2 × 48. */
export const CONTEUDO_TELA = 760;
/**
 * Largura do conteúdo no celular. A página de 760 encolhida pela metade não se
 * lia (Rick, 17/10/2026): no celular os cartões vão um embaixo do outro, os
 * gráficos são desenhados nesta largura e saem os parágrafos e as tabelas largas.
 */
export const CONTEUDO_CELULAR = 360;

export type ModoPaginas = 'pdf' | 'tela' | 'celular';

/** Largura dos gráficos de cada modo. */
export const larguraGrafico = (modo: ModoPaginas) => (modo === 'celular' ? CONTEUDO_CELULAR - 28 : 662);

/** Linha de cartões: no celular cada cartão quebra para a linha de baixo. */
export const LINHA_CARTOES = 'display:flex;flex-wrap:wrap;gap:12px;';

/** Blocos presos ao pé da folha A4 (contato da capa, próximo passo) — na tela seguem o fluxo. */
export const PRESO_AO_PE = 'position:absolute;left:48px;right:48px;bottom:70px;';

export const escapar = (v: unknown) =>
  String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

export const pct = (v: number, casas = 1) =>
  `${v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;

export const rent = (aa: number) => (!Number.isFinite(aa) ? '—' : aa > 300 ? 'acima de 300% a.a.' : `${pct(aa)} a.a.`);

/** Moldura de uma página: fundo, margens, cabeçalho e rodapé. */
export const pagina = (o: {
  conteudo: string;
  numero: number;
  total: number;
  e: Estudo;
  origin: string;
  escura?: boolean;
  modo: ModoPaginas;
}) => {
  if (o.modo !== 'pdf') return secaoTela({ ...o, celular: o.modo === 'celular' });
  const cabecalho = o.escura
    ? ''
    : `<div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:14px;border-bottom:2px solid ${COR.ouro};">
        <img src="${o.origin}/logos/logo_full.png" style="height:26px;" />
        <div style="font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:${COR.suave};">
          Apresentação de consórcio${o.e.cliente ? ` · ${escapar(o.e.cliente)}` : ''}
        </div>
      </div>`;
  const rodape = `<div style="position:absolute;left:48px;right:48px;bottom:26px;display:flex;justify-content:space-between;font-size:9px;color:${o.escura ? 'rgba(255,255,255,0.55)' : COR.suave};">
      <span>Simulação ilustrativa, sem valor contratual. Contemplação não garantida.</span>
      <span style="font-weight:700;letter-spacing:0.1em;">${String(o.numero).padStart(2, '0')} / ${String(o.total).padStart(2, '0')}</span>
    </div>`;
  const fundo = o.escura
    ? `background:radial-gradient(circle at 85% 12%, #2c3e58 0%, ${COR.marinho} 38%, ${COR.noite} 100%);color:#fff;`
    : `background:#ffffff;color:${COR.texto};`;
  return `<div style="position:relative;width:${LARGURA}px;height:${ALTURA}px;box-sizing:border-box;padding:40px 48px 60px;overflow:hidden;font-family:${FONTE_PDF};${fundo}">
    ${cabecalho}${o.conteudo}${rodape}
  </div>`;
};

/** Título de cada seção; no celular sem o parágrafo de explicação. */
export const tituloSecao = (eyebrow: string, titulo: string, sub: string, modo: ModoPaginas = 'pdf') => `
  <div style="margin:${modo === 'celular' ? '8px 0 14px' : '26px 0 18px'};">
    <div style="font-size:10.5px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;color:${COR.ouro};">${eyebrow}</div>
    <div style="font-size:${modo === 'celular' ? 23 : 27}px;font-weight:800;line-height:1.15;margin-top:6px;color:${COR.marinho};">${titulo}</div>
    ${modo === 'celular' ? '' : `<div style="font-size:12px;color:${COR.suave};margin-top:6px;line-height:1.5;">${sub}</div>`}
  </div>`;

export const caixaGrafico = (titulo: string, svg: string, legenda: Array<{ nome: string; cor: string; tracejado?: boolean }>) => `
  <div style="background:${COR.fundo};border-radius:16px;padding:16px 18px 12px;margin-top:14px;">
    <div style="display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:4px 14px;margin-bottom:6px;">
      <div style="font-size:12.5px;font-weight:700;color:${COR.marinho};">${titulo}</div>
      <div style="display:flex;flex-wrap:wrap;gap:4px 14px;">
        ${legenda
          .map(
            (l) => `<span style="display:flex;align-items:center;gap:6px;font-size:10px;color:${COR.suave};">
              <span style="display:inline-block;width:16px;height:0;border-top:3px ${l.tracejado ? 'dashed' : 'solid'} ${l.cor};"></span>${l.nome}</span>`,
          )
          .join('')}
      </div>
    </div>
    ${svg}
  </div>`;

export const cartaoNumero = (o: { rotulo: string; valor: string; detalhe?: string; escuro?: boolean; destaque?: string }) => `
  <div class="estudo-cartao" style="flex:1 1 200px;border-radius:16px;padding:16px 18px;${
    o.escuro
      ? `background:linear-gradient(135deg, ${COR.marinho}, ${COR.noite});color:#fff;`
      : `background:${COR.fundo};color:${COR.texto};`
  }border-top:4px solid ${o.destaque ?? 'transparent'};">
    <div style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;opacity:0.75;">${o.rotulo}</div>
    <div style="font-size:24px;font-weight:800;margin-top:8px;letter-spacing:-0.01em;">${o.valor}</div>
    ${o.detalhe ? `<div style="font-size:10.5px;margin-top:6px;opacity:0.75;line-height:1.4;">${o.detalhe}</div>` : ''}
  </div>`;

export const linhaTabela = (rotulo: string, valor: string, forte = false) => `
  <div class="estudo-linha" style="display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid #e6eaf0;font-size:11.5px;${forte ? 'font-weight:700;' : ''}">
    <span style="color:${forte ? COR.texto : COR.suave};">${rotulo}</span><span>${valor}</span>
  </div>`;

/**
 * Seção de largura inteira do modo tela. Sem o cabeçalho de cada folha (a
 * barra do topo da página já diz de quem é o estudo); a capa ocupa a primeira
 * tela inteira.
 */
export const secaoTela = (o: { conteudo: string; numero: number; total: number; escura?: boolean; celular?: boolean }) => {
  const fundo = o.escura
    ? `background:radial-gradient(circle at 85% 12%, #2c3e58 0%, ${COR.marinho} 38%, ${COR.noite} 100%);color:#fff;min-height:calc(100vh - 57px);display:flex;align-items:center;`
    : `background:#ffffff;color:${COR.texto};border-top:1px solid #e6eaf0;`;
  const conteudo = o.conteudo
    .split(PRESO_AO_PE)
    .join('margin-top:44px;')
    .replace(/<svg xmlns="http:\/\/www\.w3\.org\/2000\/svg" width="\d+(?:\.\d+)?" height="\d+(?:\.\d+)?"/g, '<svg xmlns="http://www.w3.org/2000/svg" style="display:block;width:100%;height:auto;"');
  return `<section style="position:relative;width:100%;box-sizing:border-box;overflow:hidden;font-family:${FONTE_PDF};${fundo}">
    <div style="zoom:var(--estudo-zoom, 1);width:${o.celular ? CONTEUDO_CELULAR : CONTEUDO_TELA}px;margin:0 auto;padding:${o.celular ? '28px 0 20px' : '44px 0 28px'};box-sizing:border-box;">
      ${conteudo}
      <div style="margin-top:${o.celular ? 24 : 36}px;display:flex;justify-content:space-between;gap:12px;font-size:9px;color:${o.escura ? 'rgba(255,255,255,0.55)' : COR.suave};">
        <span>Simulação ilustrativa, sem valor contratual. Contemplação não garantida.</span>
        <span style="font-weight:700;letter-spacing:0.1em;">${String(o.numero).padStart(2, '0')} / ${String(o.total).padStart(2, '0')}</span>
      </div>
    </div>
  </section>`;
};

/**
 * Depois da contemplação, na tela (link e editor): uma estratégia por vez, com
 * o botão que troca (estudoInterativo.ts liga o clique). As duas ficam na
 * mesma célula da grade, então o slide não muda de altura na troca. O PDF não
 * passa por aqui: lá as duas seguem uma embaixo da outra.
 */
export const duasEstrategias = (imovel: string, aplicado: string) => {
  const opcoes = [
    { n: 1, titulo: 'Comprar um imóvel e alugar', texto: 'O crédito compra o imóvel; o aluguel ajuda a pagar as parcelas.', bloco: imovel },
    { n: 2, titulo: 'Aplicar o crédito', texto: 'O crédito rende no CDI e as parcelas saem do saldo.', bloco: aplicado },
  ];
  const botoes = opcoes
    .map(
      (o) =>
        `<button type="button" role="tab" class="estudo-estrategia-botao" data-estrategia-botao="${o.n}" aria-selected="${o.n === 1}">` +
        `<span style="font-size:9px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;opacity:0.7;">Estratégia ${o.n}</span>` +
        `<span style="display:block;font-size:13px;font-weight:800;margin-top:2px;">${o.titulo}</span></button>`,
    )
    .join('');
  const paineis = opcoes
    .map(
      (o) => `<div role="tabpanel" data-estrategia="${o.n}" class="${o.n === 1 ? '' : 'estudo-oculta'}" style="grid-area:1/1;min-width:0;">
        <div style="font-size:11px;color:${COR.suave};margin-bottom:14px;line-height:1.45;">${o.texto}</div>
        ${o.bloco}
      </div>`,
    )
    .join('');
  return `<div class="estudo-estrategias">
    <div role="tablist" aria-label="Estratégia" style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px;">${botoes}</div>
    <div style="display:grid;">${paineis}</div>
  </div>`;
};
